import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { SpellCheck, History, Lock, Hash, Layers, Boxes, Link2 } from "lucide-react";
import { FamilyCard, NumberCard, MissingCard, GroupApplyCard } from "@/components/naming/QueueCards";
import { UndoButton } from "@/components/naming/NamingBits";
import { LoadMore } from "@/components/LoadMore";

const TABS = [
  { id: "tidy", label: "Tidy the list", icon: SpellCheck, path: "/naming/tidy", count: (o) => o?.tidy },
  { id: "numbers", label: "Same number, two names", icon: Hash, path: "/naming/number-conflicts", count: (o) => o?.numbers },
  { id: "lookalikes", label: "Look-alikes on parts", icon: Layers, path: "/naming/lookalikes", count: (o) => o?.lookalikes },
  { id: "missing", label: "No type / no category", icon: Boxes, path: "/naming/missing", count: (o) => o?.missing },
  { id: "group", label: "Group Apply", icon: Link2, path: "/naming/group-apply", count: (o) => o?.group_apply },
];

export default function NamingPage() {
  const { has } = useAuth();
  const canView = has("manage_lists");
  const canApply = has("merge_lists");
  const [tab, setTab] = useState("tidy");
  const [overview, setOverview] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [taxonomy, setTaxonomy] = useState({ categories: [], part_types: [] });
  const [hist, setHist] = useState(null);
  const [showHist, setShowHist] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadOverview = useCallback(() => {
    api.get("/naming/overview").then(({ data: d }) => setOverview(d)).catch(() => setOverview(null));
  }, []);
  useEffect(() => { if (canView) loadOverview(); }, [canView, loadOverview]);
  useEffect(() => { api.get("/taxonomy").then(({ data: d }) => setTaxonomy(d)).catch(() => {}); }, []);

  const current = TABS.find((t) => t.id === tab);
  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`${current.path}?page=${p}&page_size=10`);
      setData((prev) => (p > 1 && prev ? { ...d, items: [...prev.items, ...d.items] } : d));
      setPage(p);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load that queue.");
      setData({ items: [], total: 0 });
    } finally { setLoading(false); }
  }, [current.path]);
  useEffect(() => { if (canView) load(1); }, [canView, load]);

  const loadHist = useCallback(() => {
    api.get("/naming/history?page_size=15").then(({ data: d }) => setHist(d)).catch(() => setHist(null));
  }, []);
  useEffect(() => { if (showHist) loadHist(); }, [showHist, loadHist]);

  const refresh = () => { load(1); loadOverview(); if (showHist) loadHist(); };

  const undoAll = async (op) => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/naming/ops/${op.id}/undo`);
      toast.success(`Put back ${d.restored} part${d.restored === 1 ? "" : "s"}${d.skipped?.length ? ` · left ${d.skipped.length} changed since` : ""}`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not put that back."); }
    finally { setBusy(false); }
  };
  const undoOne = async (op, row) => {
    setBusy(true);
    try {
      await api.post(`/naming/ops/${op.id}/undo-part`, { part_id: row.part_id });
      toast.success(`${row.short_code || "That part"} put back`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not put that back."); }
    finally { setBusy(false); }
  };

  if (!canView) {
    return (
      <div data-testid="naming-denied" className="max-w-lg mt-10 mx-auto text-center">
        <Lock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">You need the <b>Manage lists</b> permission to work on naming.</p>
      </div>
    );
  }

  const c = current.count(overview);
  return (
    <div data-testid="naming-page" className="max-w-5xl">
      <div className="flex items-center gap-2 mb-1"><SpellCheck className="w-6 h-6 text-[#B01E28]" />
        <h1 className="font-display text-2xl font-bold tracking-tight">Naming Consistency</h1></div>
      <p className="text-sm text-slate-500 mb-4">
        Tidy the Name list first — one keeper there fixes dozens of parts at once — then work the queues.
        Nothing is ticked for you, nothing applies itself, and every part can be put back on its own.
        {!canApply && <b className="text-[#B01E28]"> You can look, but applying needs the Merge list values permission.</b>}
      </p>

      <div className="flex flex-wrap gap-1.5 mb-4 border-b border-slate-200 pb-3">
        {TABS.map((t) => {
          const Icon = t.icon; const on = tab === t.id; const n = t.count(overview);
          return (
            <button key={t.id} data-testid={`naming-tab-${t.id}`} onClick={() => { setTab(t.id); setData(null); }}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-sm font-semibold transition-colors ${on ? "bg-[#B01E28] text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-[#B01E28] hover:text-[#B01E28]"}`}>
              <Icon className="w-4 h-4" />{t.label}
              {n ? <span data-testid={`naming-count-${t.id}`} className={`ml-1 rounded-full px-1.5 text-[11px] font-mono ${on ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{n.groups}</span> : null}
            </button>
          );
        })}
        <Button data-testid="naming-history-toggle" variant="outline" onClick={() => setShowHist((v) => !v)}
          className="ml-auto rounded-lg h-9 text-xs"><History className="w-3.5 h-3.5 mr-1" />{showHist ? "Hide changes" : "Recent changes"}</Button>
      </div>

      {showHist && (
        <div data-testid="naming-history" className="mb-5 border border-slate-200 rounded-xl p-3 bg-slate-50 space-y-2">
          {!hist?.items?.length && <div className="text-xs text-slate-400">Nothing applied yet.</div>}
          {(hist?.items || []).map((op) => (
            <div key={op.id} data-testid={`naming-op-${op.id}`} className="bg-white border border-slate-200 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <Badge className="rounded-full bg-slate-100 text-slate-500 border-0 text-[10px] uppercase font-mono">{op.queue}</Badge>
                <span className="font-medium">{Object.entries(op.set).map(([k, v]) => `${k.replace("_id", "")} → ${v}`).join(" · ")}</span>
                <span className="text-[11px] text-slate-400">{op.parts} part{op.parts === 1 ? "" : "s"} · {op.created_at?.replace("T", " ").slice(0, 16)} · {op.actor_email}</span>
                {op.undone
                  ? <Badge className="ml-auto rounded-full bg-slate-200 text-slate-500 border-0 text-[10px]">put back</Badge>
                  : canApply && <span className="ml-auto"><UndoButton testid={`naming-undo-${op.id}`} busy={busy} onClick={() => undoAll(op)} label={`Put back all ${op.parts}`} /></span>}
              </div>
              {!op.undone && canApply && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {op.part_rows.map((r) => (
                    <Button key={r.part_id} data-testid={`naming-undo-part-${r.part_id}`} size="sm" variant="outline" disabled={busy}
                      onClick={() => undoOne(op, r)} className="rounded-full h-6 px-2 text-[11px] font-mono">
                      ↩ {r.short_code || r.part_id.slice(-6)}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {c && (
        <div data-testid={`naming-summary-${tab}`} className="text-xs text-slate-500 mb-3">
          {tab === "tidy" && <>{c.groups} famil{c.groups === 1 ? "y" : "ies"} of look-alike names across {c.names_in_list} values in the list · {c.parts} part{c.parts === 1 ? "" : "s"} carry them</>}
          {tab === "numbers" && <>{c.groups} number{c.groups === 1 ? "" : "s"} carrying more than one name, part type or category · {c.parts} parts</>}
          {tab === "lookalikes" && <>{c.groups} famil{c.groups === 1 ? "y" : "ies"} the list tidy cannot reach · {c.parts} parts</>}
          {tab === "missing" && <>{c.groups} name{c.groups === 1 ? "" : "s"} · {c.no_type} part{c.no_type === 1 ? "" : "s"} with no part type, {c.no_cat} with no category{c.unnamed ? ` · ${c.unnamed} more have no name at all and need naming one at a time` : ""}</>}
          {tab === "group" && <>{c.groups} identified part{c.groups === 1 ? "" : "s"} with lot-mates that match · {c.parts} parts could carry it</>}
        </div>
      )}

      {loading && !data && <div className="font-mono text-xs text-slate-400 py-10">LOADING…</div>}
      {data && data.items.length === 0 && (
        <div data-testid={`naming-empty-${tab}`} className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl p-8 text-center">
          Nothing in this queue — {tab === "tidy" ? "the Name list has no look-alike families left." : "nothing to regroup here."}
        </div>
      )}
      <div className="space-y-2">
        {(data?.items || []).map((row) => {
          if (tab === "tidy" || tab === "lookalikes") return <FamilyCard key={row.key} family={row} queue={tab} onDone={refresh} canApply={canApply} />;
          if (tab === "numbers") return <NumberCard key={row.key} group={row} taxonomy={taxonomy} onDone={refresh} canApply={canApply} />;
          if (tab === "missing") return <MissingCard key={row.key} group={row} taxonomy={taxonomy} onDone={refresh} canApply={canApply} />;
          return <GroupApplyCard key={row.key} group={row} onDone={refresh} canApply={canApply} />;
        })}
      </div>
      {data?.total > 0 && (
        <LoadMore testid="naming-load-more" shownCount={data.shown} total={data.total}
          hasMore={data.has_more} onMore={() => load(page + 1)} noun="group" />
      )}
    </div>
  );
}
